import { useCallback, useEffect, useMemo, useState } from 'react';
import { AdminRelay } from '../components/AdminRelay.tsx';
import { Icon } from '../components/Icon.tsx';
import { Link } from '../components/Link.tsx';
import { Sparkline } from '../components/Sparkline.tsx';
import { WorldMap, type MapPoint } from '../components/WorldMap.tsx';
import { Globe3D } from '../components/Globe3D.tsx';
import { fmtAtomic, useCtx } from '../lib.ts';

interface Odds {
  selection: string;
  odds: { numerator: string; denominator: string };
}
interface BetRow {
  currency: string;
  scale: number;
  selection: string;
  n: number;
  players: number;
  stake: string;
  potential: string;
  paid: string;
}
interface Current {
  gameRoundId: string;
  roundIndex: number;
  state: string;
  sourceMode: string;
  camera: { cameraId: string; name: string | null; city: string | null; countryCode: string | null; country: string | null; latitude: number | null; longitude: number | null; provider: string | null; calibrationVersion: number | null } | null;
  timing: { bettingOpenAt: string; bettingCloseAt: string; captureStartAt: string; captureEndAt: string };
  thresholds: { low: number; high: number } | null;
  odds: Odds[];
  countSoFar: number | null;
  finalCount: number | null;
  winning: string[];
  lastReason: string | null;
  bets: BetRow[];
}
interface PlanRow {
  slot: number;
  cameraId: string;
  name: string | null;
  city: string | null;
  countryCode: string | null;
  country: string | null;
  latitude: number | null;
  longitude: number | null;
  counter: string;
  countedMs: number | null;
  lagMs: number | null;
}
interface RecentRow {
  gameRoundId: string;
  roundIndex: number;
  state: string;
  cameraId: string | null;
  countryCode: string | null;
  city: string | null;
  finalCount: number | null;
  low: number | null;
  high: number | null;
  winning: string[];
  lastReason: string | null;
  createdAt: string;
  bets: number;
  players: number;
}
interface PoolRow {
  cameraId: string;
  name: string | null;
  city: string | null;
  countryCode: string | null;
  country: string | null;
  latitude: number | null;
  longitude: number | null;
  state: string;
  held: boolean;
  holdReason: string | null;
  rounds24h: number;
  warm: boolean;
}
interface View {
  now: string;
  game: { gameId: string; title: string | null; paused: boolean; mode: string | null } | null;
  current: Current | null;
  plan: PlanRow[];
  counters: { cameraId: string; status: string; lagMs: number | null; width: number | null; height: number | null }[];
  recent: RecentRow[];
  pool: PoolRow[];
  kpis: { rounds1h: { settled: number; voided: number; total: number }; rounds24h: { settled: number; voided: number }; medianGapS: number | null; money: { currency: string; scale: number; bets: number; players: number; stake: string | null; paid: string }[] } | null;
  series: { t: string; settled: number; voided: number; bets: number }[];
  incidents: { cameraId: string; kind: string; detail: string; gameRoundId: string | null; at: string }[];
}

const WARMUP_MS = 115_000;
const flag = (cc: string | null) => (cc && /^[A-Z]{2}$/.test(cc) ? String.fromCodePoint(...[...cc].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65)) : '🌐');
const short = (id: string) => id.replace(/^argus:opencctv_/, '').replace(/^[^_]+_/, '');
const ago = (iso: string, now: number) => {
  const s = Math.max(0, Math.round((now - Date.parse(iso)) / 1000));
  return s < 60 ? `${s}s ago` : s < 3600 ? `${Math.floor(s / 60)}m ago` : `${Math.floor(s / 3600)}h ago`;
};
const mmss = (ms: number) => {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};
const oddsText = (o: Odds['odds']) => `×${(Number(o.numerator) / Number(o.denominator)).toFixed(2)}`;

/** The live-camera game at a glance: the round on air, what comes next, how the pool and the money are doing. */
export function CommandCenter() {
  const { backend } = useCtx();
  const server = backend.server;
  const [view, setView] = useState<View | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [skew, setSkew] = useState(0);
  const [now, setNow] = useState(() => Date.now());
  const [focus, setFocus] = useState<string | null>(null);
  const [globe, setGlobe] = useState(true);
  const load = useCallback(async () => {
    if (!server) return;
    const r = await server.get<View>('/v1/admin/live/command-center');
    if (r.ok) {
      setView(r.value);
      setSkew(Date.parse(r.value.now) - Date.now());
      setError(null);
    } else setError(r.error);
  }, [server]);
  useEffect(() => {
    const first = setTimeout(() => void load(), 0);
    const t = setInterval(() => void load(), 3000);
    return () => (clearTimeout(first), clearInterval(t));
  }, [load]);
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(t);
  }, []);
  const post = useMemo(() => server?.post.bind(server), [server]);

  if (!server) return <p className="empty">Live operations need the UrbanRats backend (API mode).</p>;
  if (!view) return <p className="empty" aria-busy="true">{error ? `Could not load live operations: ${error}` : 'Loading live operations…'}</p>;
  const serverNow = now + skew;
  const cur = view.current;
  const points: MapPoint[] = view.pool
    .filter((c) => c.latitude !== null && c.longitude !== null)
    .map((c) => {
      const planned = view.plan.some((p) => p.cameraId === c.cameraId);
      const onAir = cur?.camera?.cameraId === c.cameraId && !['SETTLED', 'VOIDED'].includes(cur.state);
      const kind = onAir ? 'on-air' : planned ? 'planned' : c.held ? 'held' : c.state !== 'ACTIVE' ? 'standby' : c.warm ? 'warm' : 'ready';
      return { id: c.cameraId, lon: c.longitude!, lat: c.latitude!, kind, title: `${c.city ?? ''} ${c.country ?? ''} · ${short(c.cameraId)} · ${kind}${c.held && c.holdReason ? ` — ${c.holdReason}` : ''}` };
    });
  const counts = points.reduce<Record<string, number>>((m, p) => ((m[p.kind] = (m[p.kind] ?? 0) + 1), m), {});
  const focused = focus ? view.pool.find((c) => c.cameraId === focus) : null;
  const k = view.kpis;
  const voidRate = k && k.rounds24h.settled + k.rounds24h.voided > 0 ? (100 * k.rounds24h.voided) / (k.rounds24h.settled + k.rounds24h.voided) : null;

  return (
    <div className="cc" data-testid="command-center">
      <div className="cc-head">
        <div>
          <h1>Live operations</h1>
          <p className="note">
            {view.game?.title ?? view.game?.gameId} · source {view.game?.mode ?? '—'} · refreshes every 3 s · server clock {new Date(serverNow).toISOString().slice(11, 19)} UTC
          </p>
        </div>
        <div className="cc-head-tags">
          {view.game?.paused ? (
            <span className="pill warn">
              <Icon name="pause" size={14} /> Paused — no new rounds
            </span>
          ) : (
            <span className="pill live">
              <span className="live-dot" /> Running
            </span>
          )}
          {error && <span className="pill warn">refresh failed: {error}</span>}
        </div>
      </div>

      <div className="cc-grid-a">
        {cur ? <OnAir key={cur.gameRoundId} cur={cur} now={serverNow} apiBase={server.apiBase} post={post!} /> : <section className="card-x"><p className="empty">No round yet.</p></section>}
        <div className="cc-side">
          <section className="card-x" data-testid="cc-next">
            <header className="card-h">
              <h2>Up next</h2>
              <span className="note">decided ahead · warming up</span>
            </header>
            {view.plan.length === 0 && <p className="empty">No camera planned: every camera is resting or held (see the map).</p>}
            <ol className="next-list">
              {view.plan.map((p) => {
                const pct = p.countedMs ? Math.min(100, (100 * p.countedMs) / WARMUP_MS) : 0;
                return (
                  <li key={p.slot} onMouseEnter={() => setFocus(p.cameraId)} onMouseLeave={() => setFocus(null)}>
                    <Link to={`/studio?camera=${encodeURIComponent(p.cameraId)}`} className="next-link">
                      <Thumb cameraId={p.cameraId} />
                      <span className="where">
                        <strong>
                          <span className="slot">{p.slot}</span> {flag(p.countryCode)} {p.city ?? '—'}, {p.country ?? p.countryCode}
                        </strong>
                        <span className="muted">{p.name ?? short(p.cameraId)}</span>
                        <span className="warm">
                          <span className="meter" style={{ ['--p' as string]: `${pct}%` }} aria-hidden />
                          <span className="muted">{p.counter === 'RUNNING' ? (pct >= 100 ? 'ready' : `warming ${Math.round(pct)}%`) : p.counter.toLowerCase()}</span>
                        </span>
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ol>
          </section>
          <section className="card-x" data-testid="cc-counters">
            <header className="card-h">
              <h2>Live counters</h2>
              <span className="note">{view.counters.length} running</span>
            </header>
            <ul className="counter-list">
              {view.counters.map((c) => (
                <li key={c.cameraId}>
                  <span className={`live-dot ${c.status === 'RUNNING' ? 'ok' : 'idle'}`} aria-hidden />
                  <span className="mono">{short(c.cameraId)}</span>
                  <span className="muted">{c.width && c.height ? `${c.height}p` : ''}</span>
                  <span className={`lag ${c.lagMs !== null && c.lagMs > 25_000 ? 'bad' : ''}`}>{c.lagMs !== null ? `${(c.lagMs / 1000).toFixed(1)} s behind` : '—'}</span>
                </li>
              ))}
              {view.counters.length === 0 && <li className="muted">No counter running.</li>}
            </ul>
          </section>
        </div>
      </div>

      {k && (
        <div className="cc-kpis" data-testid="cc-kpis">
          <Tile label="Rounds · last hour" value={String(k.rounds1h.settled)} sub={`${k.rounds1h.voided} voided · ${k.rounds1h.total} opened`} spark={view.series.map((s) => s.settled)} tone="green" to="/rounds" />
          <Tile label="Median time between rounds" value={k.medianGapS !== null ? mmss(k.medianGapS * 1000) : '—'} sub="last hour, open → open" to="/rounds" />
          <Tile label="Void rate · 24 h" value={voidRate !== null ? `${voidRate.toFixed(1)} %` : '—'} sub={`${k.rounds24h.voided} of ${k.rounds24h.settled + k.rounds24h.voided} rounds refunded`} spark={view.series.map((s) => s.voided)} tone="red" bars to="/rounds?state=VOIDED" />
          <Tile label="Bets · 24 h" value={String(k.money.reduce((a, m) => a + m.bets, 0))} sub={`${k.money.reduce((a, m) => a + m.players, 0)} players (per currency)`} spark={view.series.map((s) => s.bets)} tone="cyan" bars to="/bets" />
          {k.money.map((m) => {
            const stake = BigInt(m.stake ?? '0');
            const paid = BigInt(m.paid);
            return <Tile key={m.currency} label={`GGR · 24 h · ${m.currency}`} value={fmtAtomic(stake - paid, m.scale, m.currency)} sub={`stake ${fmtAtomic(stake, m.scale, m.currency)} · paid ${fmtAtomic(paid, m.scale, m.currency)}`} negative={stake < paid} to="/finance" />;
          })}
          {k.money.length === 0 && <Tile label="GGR · 24 h" value="—" sub="no settled bets in 24 h" to="/finance" />}
        </div>
      )}

      <div className="cc-grid-b">
        <section className="card-x map-card" data-testid="cc-map">
          <header className="card-h">
            <h2>Camera pool</h2>
            <span className="legend">
              {['on-air', 'planned', 'ready', 'warm', 'held', 'standby'].map((x) => (
                <span key={x} className={`lg lg-${x}`}>
                  <i aria-hidden /> {x} {counts[x] ?? 0}
                </span>
              ))}
            </span>
          </header>
          <div className="seg-ctl map-toggle" role="group" aria-label="Map view">
            <button type="button" className={globe ? 'on' : ''} onClick={() => setGlobe(true)}>
              3D
            </button>
            <button type="button" className={!globe ? 'on' : ''} onClick={() => setGlobe(false)}>
              Flat
            </button>
          </div>
          {globe ? <Globe3D points={points} /> : <WorldMap points={points} focus={focus} onPick={(id) => setFocus(id === focus ? null : id)} />}
          {focused && (
            <div className="map-focus">
              <span className="flag" aria-hidden>
                {flag(focused.countryCode)}
              </span>
              <span>
                <strong>
                  {focused.city ?? '—'}, {focused.country}
                </strong>{' '}
                <span className="muted">· {focused.name ?? short(focused.cameraId)}</span>
                <br />
                <span className="muted">
                  {focused.state} · {focused.rounds24h} rounds in 24 h{focused.held && focused.holdReason ? ` · held: ${focused.holdReason}` : ''}
                </span>
              </span>
              <Link to="/camera-pipeline" className="btn ghost">
                Pipeline →
              </Link>
            </div>
          )}
        </section>
        <section className="card-x" data-testid="cc-recent">
          <header className="card-h">
            <h2>Recent rounds</h2>
            <Link to="/rounds" className="note">
              all rounds →
            </Link>
          </header>
          <ul className="recent-list">
            {view.recent.slice(0, 9).map((r) => (
              <li key={r.gameRoundId}>
                <span className={`res ${r.state === 'VOIDED' ? 'void' : r.finalCount === null ? 'open' : ''}`}>{r.state === 'VOIDED' ? '∅' : (r.finalCount ?? '…')}</span>
                <span className="where">
                  <strong>
                    {flag(r.countryCode)} {r.city ?? '—'}
                  </strong>
                  <span className="muted">
                    {r.state === 'VOIDED' ? (r.lastReason ?? 'voided') : r.low !== null && r.high !== null ? `bands ${r.low}/${r.high}${r.winning.length ? ` · ${r.winning.join(', ')}` : ''}` : r.state}
                  </span>
                </span>
                <span className="muted small">
                  {r.bets} bets
                  <br />
                  {ago(r.createdAt, serverNow)}
                </span>
              </li>
            ))}
          </ul>
        </section>
      </div>

      <section className="card-x" data-testid="cc-incidents">
        <header className="card-h">
          <h2>Incidents</h2>
          <span className="note">camera problems the pipeline recorded (newest first)</span>
        </header>
        <ul className="incident-list">
          {view.incidents.slice(0, 10).map((i, n) => (
            <li key={n}>
              <span className={`tag st-${i.kind}`}>{i.kind}</span>
              <span className="mono">{short(i.cameraId)}</span>
              <span className="detail">{i.detail}</span>
              <span className="muted small">{ago(i.at, serverNow)}</span>
            </li>
          ))}
          {view.incidents.length === 0 && <li className="muted">No incidents.</li>}
        </ul>
      </section>
    </div>
  );
}

/** Latest picture of a planned camera (from our ingest buffer), refreshed every 20 s. */
function Thumb({ cameraId }: { cameraId: string }) {
  const { backend } = useCtx();
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    let current: string | null = null;
    const load = () =>
      void backend.server?.blob(`/v1/admin/cameras/${encodeURIComponent(cameraId)}/preview.jpg`).then((r) => {
        if (!live || !r.ok) return;
        if (current) URL.revokeObjectURL(current);
        current = r.value.url;
        setUrl(r.value.url);
      });
    const first = setTimeout(load, 0);
    const t = setInterval(load, 20_000);
    return () => {
      live = false;
      clearTimeout(first);
      clearInterval(t);
      if (current) URL.revokeObjectURL(current);
    };
  }, [backend, cameraId]);
  return <span className="thumb">{url ? <img src={url} alt="" /> : <span className="thumb-wait">…</span>}</span>;
}

function Tile({ label, value, sub, spark, tone = 'brand', bars = false, negative = false, to }: { label: string; value: string; sub: string; spark?: number[]; tone?: 'brand' | 'green' | 'red' | 'cyan'; bars?: boolean; negative?: boolean; to: string }) {
  return (
    <Link to={to} className={`tile ${negative ? 'neg' : ''}`}>
      <span className="tile-label">{label}</span>
      <strong className="tile-value">{value}</strong>
      <span className="tile-sub">{sub}</span>
      {spark && <Sparkline values={spark} tone={tone} bars={bars} />}
    </Link>
  );
}

const BOARD: { sel: string; label: string }[] = [
  { sel: 'UNDER', label: 'Few' },
  { sel: 'EXACT_LOW', label: 'Exactly' },
  { sel: 'RANGE', label: 'Middle' },
  { sel: 'EXACT_HIGH', label: 'Exactly' },
  { sel: 'OVER', label: 'Many' },
  { sel: 'EXACT', label: 'Either limit' },
];

function OnAir({ cur, now, apiBase, post }: { cur: Current; now: number; apiBase: string; post: <T>(path: string, body: Record<string, unknown>) => Promise<{ ok: true; value: T } | { ok: false; error: string }> }) {
  // The phase and the count follow the picture on screen (it runs behind the camera); server clock otherwise.
  const [pic, setPic] = useState<{ wallMs: number | null; count: number } | null>(null);
  const terminal = ['SETTLED', 'VOIDED'].includes(cur.state);
  const picMs = !terminal && pic?.wallMs != null ? pic.wallMs : null;
  const clock = picMs ?? now;
  const t = cur.timing;
  const open = Date.parse(t.bettingOpenAt);
  const close = Date.parse(t.bettingCloseAt);
  const start = Date.parse(t.captureStartAt);
  const end = Date.parse(t.captureEndAt);
  const phase = now < close ? 'Betting open' : clock < start ? 'Countdown' : clock < end ? 'Counting' : cur.state === 'VOIDED' ? 'Voided' : cur.state === 'SETTLED' ? 'Settled' : 'Result';
  const left = now < close ? close - now : clock < start ? start - clock : clock < end ? end - clock : 0;
  const total = end - open;
  const pos = (x: number) => `${Math.min(100, Math.max(0, (100 * (x - open)) / total))}%`;
  const L = cur.thresholds?.low ?? null;
  const H = cur.thresholds?.high ?? null;
  const boardCounts: Record<string, string> =
    L !== null && H !== null ? { UNDER: `≤ ${L - 1}`, EXACT_LOW: `${L}`, RANGE: `${L + 1}–${H - 1}`, EXACT_HIGH: `${H}`, OVER: `≥ ${H + 1}`, EXACT: `${L} / ${H}` } : {};
  const count = picMs !== null && picMs < end ? pic!.count : (cur.finalCount ?? cur.countSoFar);
  const currencies = [...new Set(cur.bets.map((b) => b.currency))];
  const isLive = cur.sourceMode === 'LIVE' && !terminal;
  return (
    <section className="card-x on-air" data-testid="cc-on-air">
      <header className="card-h">
        <h2>
          {isLive ? <span className="live-dot" aria-hidden /> : null} On air
        </h2>
        <Link to={`/rounds/${encodeURIComponent(cur.gameRoundId)}`} className="mono">
          {cur.gameRoundId}
        </Link>
        <span className={`tag st-${cur.state}`}>{cur.state}</span>
        <span className="spacer" />
        {cur.camera && (
          <span className="where-inline">
            {flag(cur.camera.countryCode)} <strong>{cur.camera.city ?? '—'}</strong>, {cur.camera.country} <span className="muted">· {cur.camera.name ?? short(cur.camera.cameraId)}</span>
          </span>
        )}
      </header>
      <div className="stage">
        {isLive ? <AdminRelay roundId={cur.gameRoundId} apiBase={apiBase} post={post} onPicture={setPic} /> : <div className="relay relay-off">{terminal ? `Round ${cur.state.toLowerCase()} — next round being prepared` : 'Not a live-camera round'}</div>}
        <div className="hud">
          <div className="hud-count">
            <span>{picMs !== null ? 'count on screen' : terminal ? 'final count' : 'count'}</span>
            <strong>{count ?? '—'}</strong>
          </div>
          <div className="hud-phase">
            <span>{phase}</span>
            <strong>{left > 0 ? mmss(left) : '—'}</strong>
          </div>
        </div>
      </div>
      <div className="phase-bar" aria-hidden>
        <span className="seg bet" style={{ left: '0%', width: pos(close) }} />
        <span className="seg cd" style={{ left: pos(close), width: `calc(${pos(start)} - ${pos(close)})` }} />
        <span className="seg cnt" style={{ left: pos(start), width: `calc(100% - ${pos(start)})` }} />
        <span className="now" style={{ left: pos(now < close ? now : clock) }} />
      </div>
      <div className="phase-labels">
        <span>betting {new Date(open).toISOString().slice(11, 19)}</span>
        <span>closes {new Date(close).toISOString().slice(11, 19)}</span>
        <span>
          counts {new Date(start).toISOString().slice(11, 19)}–{new Date(end).toISOString().slice(11, 19)} UTC
        </span>
      </div>
      {L !== null && (
        <div className="board" data-testid="cc-board">
          {BOARD.map(({ sel, label }) => {
            const o = cur.odds.find((x) => x.selection === sel);
            if (!o) return null;
            const win = cur.winning.includes(sel);
            return (
              <div key={sel} className={`cell ${sel === 'EXACT_LOW' || sel === 'EXACT_HIGH' || sel === 'EXACT' ? 'exact' : ''} ${win ? 'win' : ''}`}>
                <span className="cell-label">{label}</span>
                <strong>{boardCounts[sel]}</strong>
                <span className="cell-odds">{oddsText(o.odds)}</span>
                {currencies.map((c) => {
                  const b = cur.bets.find((x) => x.currency === c && x.selection === sel);
                  return (
                    <span key={c} className="cell-bets">
                      {b ? `${b.n} · ${fmtAtomic(BigInt(b.stake), b.scale, c)}` : `0 · ${c}`}
                    </span>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
      {cur.state === 'VOIDED' && cur.lastReason && <p className="warn-box">Voided and refunded: {cur.lastReason}</p>}
    </section>
  );
}
